import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Props {
  matterId: string;
  subsetId: string;
}

export const useDownloadDocumentsList = ({ matterId, subsetId }: Props) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadDocumentsList = async ({
    format,
    title,
    columns,
    relativityColumns,
    searchQuery,
    processed,
    extensions,
    startDate,
    endDate,
    documentTypes,
    documentIssues,
    hasKeyFacts,
    hasRelevantFacts,
    isPleading,
    metadataFilters,
  }: {
    format: string;
    title: string;
    columns: string[];
    relativityColumns?: string[];
    searchQuery?: string;
    processed?: boolean;
    extensions?: string[];
    startDate?: string;
    endDate?: string;
    documentTypes?: string[];
    documentIssues?: string[];
    hasKeyFacts?: boolean;
    hasRelevantFacts?: boolean;
    isPleading?: boolean;
    metadataFilters?: { field: string; value: string | number | string[] | number[] }[];
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        format,
        title,
        columns,
        relativityColumns,
        searchQuery,
        processed,
        extensions,
        startDate,
        endDate,
        documentTypes,
        documentIssues,
        hasKeyFacts,
        hasRelevantFacts,
        isPleading,
        metadataFilters,
      },
    });

    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/download-list/`,
      fetchConfig,
    );

    if (!response.ok) {
      throw new Error('Failed to download document index');
    }

    const blob = await response.blob();

    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${title}.${format}`,
    });

    link.click();

    URL.revokeObjectURL(link.href);
  };

  const downloadDocumentsWritten = async ({
    title,
    style,
    format,
    documentOptions,
  }: {
    title: string;
    style?: string;
    format: DownloadFormat;
    documentOptions?: DocumentDownloadOptions;
  }): Promise<{ exportId: string; jobId: string }> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        format,
        styleFormat: StyleFormat.Written,
        title,
        style,
        documentOptions,
      },
    });

    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/download-list/`,
      fetchConfig,
    );

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start document list export');
    }

    return await response.json();
  };

  const downloadExport = async (title: string, exportId: string, format?: DownloadFormat): Promise<void> => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.GET });
    const formatParam = format ? `&format=${format}` : '';
    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/docs/list-export-download-url/${subsetId}?exportId=${exportId}${formatParam}`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) throw new Error('Failed to get document list export download URL');

    const data: { downloadUrl: string } = await response.json();
    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = format ? `${title}.${format}` : `${title}.docx`;
    link.click();
  };

  const {
    mutate: fetchDownload,
    mutateAsync: fetchDownloadAsync,
    isLoading: isLoadingDownload,
  } = useMutation(downloadDocumentsList, {
    onError: () => {
      toast.error('Failed to download document index. Please try again.');
    },
  });

  const writtenMutation = useMutation(downloadDocumentsWritten, {
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to download document list. Please try again.');
    },
  });

  return { fetchDownload, fetchDownloadAsync, isLoadingDownload, writtenMutation, downloadExport };
};
